// Pure popup field-presentation logic for ArcGIS feature layers: merges the sublayer metadata's
// fields[] (name/alias/type) with the ArcGIS Online item's popupInfo (authored visibility,
// labels, number formats, title template) into one ordered field list the popup builder renders
// from. Everything here is data-shaping — no fetch, no DOM, no HTML (the builder escapes).

// Field types Map Viewer hides by default; an item fieldInfo that explicitly marks one visible
// still shows it (author's call).
const HIDDEN_FIELD_TYPES = new Set(["esriFieldTypeOID", "esriFieldTypeGlobalID", "esriFieldTypeGeometry"]);

// Injected date rendering: { formatUtcDate, formatDate, formatDateTime }, each (epochMs) => string.
// The track bundle registers a user-preference implementation (map-base.js: tickOffset +
// user.dateFormat, the app-wide convention for showing instants in the user's configured
// timezone/format); left null (admin preview, tests), dates fall back to browser-local rendering.
// This module stays the decider of WHICH rendering applies (see formatDateValue); the formatter
// only supplies HOW — keeping the module free of moment/user singletons the admin bundle lacks.
let dateFormatter = null;

export function setDateFormatter(formatter) {
	dateFormatter = formatter;
}

const MILLIS_PER_DAY = 86400000;

// ArcGIS date fields conflate two kinds of values. A date-ONLY value is stored as midnight UTC —
// rendering it in any timezone west of UTC shows the previous day, so it must be shown as its UTC
// calendar date, unshifted. Anything else is a real instant and renders in the user's preferred
// timezone: date-only when the authored format says so (every time-bearing ArcGIS dateFormat name
// contains "Time"), else date + time.
function formatDateValue(value, format, locale) {
	if (value % MILLIS_PER_DAY === 0) {
		return dateFormatter != null
			? dateFormatter.formatUtcDate(value)
			: new Date(value).toLocaleDateString(locale, { timeZone: "UTC" });
	}
	if (format != null && typeof format.dateFormat === "string" && !/time/i.test(format.dateFormat)) {
		return dateFormatter != null
			? dateFormatter.formatDate(value)
			: new Date(value).toLocaleDateString(locale);
	}
	return dateFormatter != null
		? dateFormatter.formatDateTime(value)
		: new Date(value).toLocaleString(locale);
}

function serviceFieldMap(serviceFields) {
	const byName = new Map();
	if (Array.isArray(serviceFields)) {
		for (const field of serviceFields) {
			if (field != null && typeof field.name === "string" && field.name !== "") {
				byName.set(field.name.toLowerCase(), field);
			}
		}
	}
	return byName;
}

function fieldAlias(serviceField) {
	if (serviceField != null && typeof serviceField.alias === "string" && serviceField.alias !== "") {
		return serviceField.alias;
	}
	return null;
}

function fieldLabel(explicitLabel, serviceField, fieldName) {
	if (typeof explicitLabel === "string" && explicitLabel !== "") {
		return explicitLabel;
	}
	if (serviceField != null && typeof serviceField.alias === "string" && serviceField.alias !== "") {
		return serviceField.alias;
	}
	return fieldName;
}

// The presentation plan for a sublayer's popup, or null when neither source provides anything
// (callers then fall back to the raw property dump). With popupInfo fieldInfos, the authored
// visibility/order/labels/formats win; otherwise every non-system service field shows with its
// alias. A fieldInfos config that hides every field (description-only popups, which this app does not
// render) deliberately falls back to that same alias dump — the popup body here is the property
// table, and an empty one serves nobody.
// The title is the popupInfo's template when set, else the service displayField as a
// bare {field} template, else null.
export function buildFieldPresentation(serviceFields, popupInfo, displayField) {
	const byName = serviceFieldMap(serviceFields);
	// visible !== false: ArcGIS treats an omitted visible flag as visible (programmatically
	// authored items commonly leave it undefined), so only an explicit false hides a field.
	const fieldInfos = popupInfo != null && Array.isArray(popupInfo.fieldInfos)
		? popupInfo.fieldInfos.filter((info) => info != null && info.visible !== false && typeof info.fieldName === "string" && info.fieldName !== "")
		: [];
	const fields = [];
	if (fieldInfos.length > 0) {
		for (const info of fieldInfos) {
			const service = byName.get(info.fieldName.toLowerCase());
			fields.push({
				name: info.fieldName,
				alias: fieldAlias(service),
				label: fieldLabel(info.label, service, info.fieldName),
				type: service != null ? service.type : null,
				format: info.format != null && typeof info.format === "object" ? info.format : null
			});
		}
	} else {
		for (const field of byName.values()) {
			if (HIDDEN_FIELD_TYPES.has(field.type) || field.name.startsWith("Shape__")) {
				continue;
			}
			fields.push({
				name: field.name,
				alias: fieldAlias(field),
				label: fieldLabel(null, field, field.name),
				type: field.type,
				format: null
			});
		}
	}
	if (fields.length === 0) {
		return null;
	}
	let title = null;
	if (popupInfo != null && typeof popupInfo.title === "string" && popupInfo.title.trim() !== "") {
		title = popupInfo.title;
	} else if (typeof displayField === "string" && displayField !== "") {
		title = `{${displayField}}`;
	}
	return { title: title, fields: fields };
}

// Display value for one field. Date fields (epoch millis) render via formatDateValue — UTC
// calendar date for date-only values, the user's time preference for instants. Other numbers
// reformat ONLY when the author asked: grouping with an explicit digitSeparator: true, pinned
// decimals with places. Without an authored format the value passes through verbatim —
// identifier-like numbers (FIPS, ZIP, years) must read and copy exactly as stored.
// Anything else passes through untouched — the popup builder escapes it.
// `locale` is for tests; production callers omit it and get the browser locale.
export function formatFieldValue(value, field, locale) {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return value;
	}
	if (field.type === "esriFieldTypeDate") {
		return formatDateValue(value, field.format, locale);
	}
	const format = field.format;
	if (format == null) {
		return value;
	}
	const options = { useGrouping: format.digitSeparator === true };
	if (Number.isFinite(format.places)) {
		// places is untrusted item JSON; out-of-range values make toLocaleString throw RangeError.
		// 20 is the largest fraction-digit count every engine accepts.
		const places = Math.min(Math.max(Math.trunc(format.places), 0), 20);
		options.minimumFractionDigits = places;
		options.maximumFractionDigits = places;
	}
	return value.toLocaleString(locale, options);
}

// Resolves a popupInfo-style "{FIELD_NAME}" template against a feature's properties
// (case-insensitive, matching ArcGIS's behavior). When the presentation's `fields` are supplied,
// placeholder values render through formatFieldValue so titles agree with their table rows (dates
// as dates, not raw epoch millis). Unknown placeholders — including {expression/exprN}, which we
// don't evaluate — become empty. Null when the template is blank or resolves to nothing but
// whitespace/punctuation-free emptiness.
export function substituteFieldTemplate(template, properties, fields) {
	if (typeof template !== "string" || template === "") {
		return null;
	}
	const lookup = new Map();
	if (properties != null) {
		for (const name in properties) {
			if (Object.prototype.hasOwnProperty.call(properties, name)) {
				lookup.set(name.toLowerCase(), properties[name]);
			}
		}
	}
	const fieldByName = new Map();
	if (Array.isArray(fields)) {
		for (const field of fields) {
			if (field != null && typeof field.name === "string") {
				fieldByName.set(field.name.toLowerCase(), field);
			}
		}
	}
	const result = template.replace(/\{([^{}]+)\}/g, (match, name) => {
		const key = name.trim().toLowerCase();
		const value = lookup.get(key);
		if (value == null) {
			return "";
		}
		const field = fieldByName.get(key);
		const formatted = field != null ? formatFieldValue(value, field) : value;
		return formatted == null ? "" : String(formatted);
	}).replace(/\s+/g, " ").trim().replace(/^[,\s]+|[,\s]+$/g, "");
	return result === "" ? null : result;
}

// Re-keys alias-keyed attributes to their field names so a presentation joins them. The ArcGIS
// identify operation (image-mode popups) keys attributes by field ALIAS, while the presentation's
// field matching and {FIELD} title templates resolve by field NAME. Only keys matching a
// presentation field's alias are renamed (case-insensitively), and never over an existing
// name-keyed property; everything else passes through untouched, so the raw-dump fallback is
// unaffected. Feature-mode attributes (name-keyed) come back unchanged.
export function remapAliasKeyedProperties(properties, presentation) {
	if (properties == null || presentation == null || !Array.isArray(presentation.fields) || presentation.fields.length === 0) {
		return properties;
	}
	const aliasToName = new Map();
	for (const field of presentation.fields) {
		if (typeof field.alias === "string" && field.alias !== "" && field.alias.toLowerCase() !== field.name.toLowerCase()) {
			aliasToName.set(field.alias.toLowerCase(), field.name);
		}
	}
	if (aliasToName.size === 0) {
		return properties;
	}
	const presentKeys = new Set();
	for (const key in properties) {
		if (Object.prototype.hasOwnProperty.call(properties, key)) {
			presentKeys.add(key.toLowerCase());
		}
	}
	const remapped = {};
	for (const key in properties) {
		if (!Object.prototype.hasOwnProperty.call(properties, key)) {
			continue;
		}
		const name = aliasToName.get(key.toLowerCase());
		remapped[name != null && !presentKeys.has(name.toLowerCase()) ? name : key] = properties[key];
	}
	return remapped;
}
